import React, { useRef, Fragment } from "react";
import { Row, Col, Button } from "react-bootstrap";
import Formsy from "formsy-react";
import { useAsyncSetState } from "use-async-setstate";
import { useMutation, useQuery } from "@apollo/client";
import "../../style/custom/update-account.scss";
import TextField from "../bootstrap/input";
import Modal from "../bootstrap/modal";
import { updateAccountMutation, createAccountMutation, getCurrentUserQuery} from "../logic/user";
import { getSiteInfoQuery, getSiteInfoResult } from "../logic/purchase";
import moment from "moment-mini";
import {getOperationName} from "apollo-utilities";

export default function UpdateCC(props) {
  const [state, setState] = useAsyncSetState({
    processing: false,
    error: "",
  });

  const [error, setError] = useAsyncSetState(null);
  const [canSubmit, setCanSubmit] = useAsyncSetState(false);
  const { onClose, user } = props;
  const formRef = useRef(null);

  const accountId = (user?.accounts?.edges || [])[0]?.node?.id;
  const [mutation] = useMutation(accountId ? updateAccountMutation : createAccountMutation);

  const siteInfoQuery = useQuery(getSiteInfoQuery);
  if (siteInfoQuery.loading) {
    return (<Fragment/>);
  }
  const siteInfo = getSiteInfoResult(siteInfoQuery);
  const ewayCryptKey = siteInfo.auEwayClientKey;

  async function handleSubmit({ cardNumber, cardName, cardCCV, expiryMonth, expiryYear }) {
    try {
      let expiry = moment(
        `${expiryMonth}${expiryYear}`,
        "MMYY"
      );
      const isValid = expiry.isValid() && moment(expiry).isSameOrAfter(moment().startOf("month"));

      if (!isValid) {
        return setState({...state, error: "Invalid Card Expiry"});
      }

      await setState((prevState) => ({ ...prevState, processing: true, error: "" }));
      await mutation({
        variables: {
          accountId,
          userId: user?.id,
          name: `${user?.firstName || ""} ${user?.lastName || ""}`,
          ccard: {
            card: window.eCrypt.encryptValue(cardNumber, ewayCryptKey),
            cvn: window.eCrypt.encryptValue(cardCCV, ewayCryptKey),
            name: cardName,
            month: expiryMonth,
            year: expiryYear,
          },
        },
        awaitRefetchQueries: true,
        refetchQueries: [getOperationName(getCurrentUserQuery)],
      });

      await setState((prevState) => ({ ...prevState, processing: false }));
      console.log("success");
      return true;
    } catch (err) {
      await setError((err?.meta?.graphQLErrors || [])[0]?.message || err.message || "Something went wrong");
      return setState((prevState) => ({ ...prevState, processing: false }));
    }
  }

  return (
    <Modal
      show
      onClose={async () => onClose()}
    >
      <Formsy className="update-account-cc-form" 
        ref={formRef}
        onValid={() => setCanSubmit(true)}
        onValidSubmit={async (data) => handleSubmit(data)}
      >
        {error && (
          <Row>
            <Col>
              <div className="alert alert-danger mt-0">{error}</div>
            </Col>
          </Row>
        )}
        <div className="update-account-cc-container">
          <div className="update-account-cc-heading">
            <p className="update-account-cc-title">{"Update Credit Card"}</p>
          </div>
          <div className="field-row-cc-holder-name">
            <TextField
              hideRequiredMark="true"
              placeholder={"Cardholder Name"}
              label={"Cardholder Name"}
              name="cardName"
              disabled={state.processing}
              required
              validations={{
                maxLength: 50,
                isExisty: true,
                matchRegexp: /^[a-zA-Z-,]+(\s{0,1}[a-zA-Z-, ])*$/,
              }}
              validationErrors={{
                isExisty: "This field is required",
                maxLength: "You can not type in more than 50 characters",
                matchRegexp: "Enter the name on your credit card",
              }}
            />
          </div>
          <div className="field-row-cc-number">
            <TextField
              name="cardNumber"
              hideRequiredMark="true"
              placeholder={"Card Number"}
              label={"Card Number"}
              validations={{
                maxLength: 16,
                minLength: 16,
                isNumeric: true,
                isExisty: true,
              }}
              validationErrors={{
                isNumeric: "You can only enter in numerical characters",
                isExisty: "This field is required",
                maxLength: "You can not type in more than 16 characters",
                minLength: "You can not type in less than 16 characters",
              }}
              required
            />
          </div>
          <div className="field-row-cc-access-details">
            <div className="cc-expiry-container">

              <div className="cc-expiry-month">
                <label className="text-field-base-label">{"Expiry Month"}</label>
                <TextField
                  hideRequiredMark="true"
                  name="expiryMonth"
                  placeholder={"MM"}
                  validations={{
                    maxLength: 2,
                    minLength: 2,
                    isNumeric: true,
                    isExisty: true,
                    invalidMonthValue: (__, value) => {
                      return value <= 12 && value > 0; // Checks if valid month
                    },
                    isExpired: (values, value) => {
                      const currentMonth = Number(new Date()
                        .getMonth()
                        .toString()
                        .slice(-2)) + 1;
                      const currentYear = new Date()
                        .getFullYear()
                        .toString()
                        .slice(-2);

                      return (
                        // Validates if card is expired
                        values.expiryYear === undefined ||
                        values.expiryYear.toString().trim() === "" ||
                        Number(values.expiryYear) > Number(currentYear) ||
                        (Number(
                          currentMonth > 10 ? `0${currentMonth}` : currentMonth
                        ) <= Number(value)) && Number(values.expiryYear) >= Number(currentYear)
                      );
                    },
                  }}
                  validationErrors={{
                    minLength: "You can not type in less than 2 characters",
                    maxLength: "You can not type in more than 2 characters",
                    isNumeric: "You can only enter in numerical characters",
                    isExisty: "This field is required",
                    invalidMonthValue: "Cannot input invalid month",
                    isExpired: "Card already expired",
                  }}
                  disabled={state.processing}
                  required
                />
              </div>
              <span className="cc-date-slash">{"/"}</span>
              <div className="cc-expiry-year">
              <label className="text-field-base-label">{"Expiry Year"}</label>
                <TextField
                  hideRequiredMark="true"
                  name="expiryYear"
                  placeholder={"YY"}
                  validations={{
                    maxLength: 2,
                    minLength: 2,
                    isNumeric: true,
                    isExisty: true,
                  }}
                  validationErrors={{
                    minLength: "You can not type in less than 2 characters",
                    maxLength: "You can not type in more than 2 characters",
                    isNumeric: "You can only enter in numerical characters",
                    isExisty: "This field is required",
                  }}
                  disabled={state.processing}
                  required
                />
              </div>
            </div>
            
            <div className="cc-cvc-container">
              <TextField
                hideRequiredMark="true"
                name="cardCCV"
                placeholder={"CCV"}
                label={"CCV"}
                validations={{
                  minLength: 3,
                  maxLength: 3,
                  isExisty: true,
                  isNumeric: true,
                }}
                validationErrors={{
                  isNumeric: "You can only enter in numerical characters",
                  isExisty: "CCV field is required",
                  minLength: "You can not type in less than 3 characters",
                  maxLength: "You can not type in more than 2 characters",
                }}
                disabled={state.processing}
                required
              />
            </div>
          </div>

          <div className="update-account-cc-actions">
            <div className="update-account-buttons">
              <div className="cancel-update">
                <Button
                  type="button"
                  variant="black-text"
                  disabled={state.processing}
                  onClick={async () => {
                    return onClose();
                  }}
                >
                  {"Cancel"}
                </Button>
              </div>
              <div className="credit-update">
                <Button
                  className="blue-btn-outline"
                  type="submit"
                  disabled={state.processing || !canSubmit}
                  onClick={() => formRef.current.submit()}
                >
                  {"Save"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Formsy>
    </Modal>
  );
}